import React, { useState, useEffect } from 'react';
import { object } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { map, get, find, filter, head, size, isNil, includes, orderBy } from 'lodash';
import moment from 'moment';
import { FlexContainer, Accordion, Text, Loading, If, NotFound, HasPermissions, Main, Icon, Button } from '../../common';
import PubSub from '../../../services/PubSub';
import SlaCard from './SlaCard';
import MediaListener from '../../utils/MediaListener';
import Filters from './Filters';
import { Query, ModuleAccessLogger } from '../../graphql';
import GET_ORDERS_COUNTERS from '../../../queries/GET_ORDERS_COUNTERS';
import { arrayIsNill } from '../../../utils/misc';
import { withApollo } from 'react-apollo';
import { withFirebase } from '../../../hoc';
import SlaCardMobile from './SlaCardMobile';
import MobilePageTitle from '../../common/MobilePageTitle';
import HorizontalResume from './HorizontalResume';

const OrdersMonitor = ({ session, client, firebasePerformance }) => {
    const sessionSalesChannels = map(get(session, 'salesChannels', []), ({ id, name, kind }) => ({ value: id, label: name, kind }));

    // Estado para almacenar los datos de contadores para exportar
    const [countersData, setCountersData] = useState(null);

    const handleExport = () => {
        if (!countersData) return;

        const bySalesChannel = get(countersData, 'bySalesChannel', []);
        
        // Definir las columnas del CSV
        const headers = [
            'CodigoTienda',
            'NombreTienda',
            'Pendiente',
            'En picking',
            'Por Auditar',
            'Por Facturar',
            'Error facturar',
            'Facturado',
            'Anulado',
            'Total'
        ];

        // Generar filas del CSV usando el resumen de cada tienda
        const rows = bySalesChannel.map(store => {
            const storeName = get(store, 'name', 'Sin nombre');
            const resumeData = get(store, 'resume', {});
            
            // Extraer código y nombre de la tienda
            // Formato esperado: "J513 - Jumbo El Llano"
            const match = storeName.match(/^([^\s-]+)\s*-\s*(.+)$/);
            const storeCode = match ? match[1].trim() : '';
            const storeNameClean = match ? match[2].trim() : storeName;
            
            return [
                storeCode,
                storeNameClean,
                get(resumeData, 'pendientePicking', 0),
                get(resumeData, 'inPicking', 0),
                get(resumeData, 'pendienteAuditoria', 0),
                get(resumeData, 'pendienteFacturacion', 0),
                get(resumeData, 'errorFacturar', 0),
                get(resumeData, 'facturado', 0),
                get(resumeData, 'anulado', 0),
                get(resumeData, 'total', 0)
            ];
        });

        // Crear contenido CSV
        const csvContent = [
            headers.join(';'),
            ...rows.map(row => row.join(';'))
        ].join('\n');

        // Crear y descargar el archivo
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `resumen_tiendas_${moment().format('YYYY-MM-DD_HH-mm')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const rightHeader = () => {
        return (
            <FlexContainer direction='column'>
                <FlexContainer direction='row'>
                    <HasPermissions to={['orders-monitor:export']}>
                        <Button width={100} text='Exportar' secondary onClick={handleExport} disabled={!countersData} />
                    </HasPermissions>
                </FlexContainer>
            </FlexContainer>
        );
    };

    useEffect(() => {
        PubSub.dispatch('SET_RIGHT_HEADER', () => rightHeader());
        return () => {
            PubSub.dispatch('SET_RIGHT_HEADER', () => null);
        };
    }, [countersData]);

    useEffect(() => {
        setPageReload(true);
        setSelectedSalesChannels(sessionSalesChannels);
        setDateRange([moment(), moment()]);
        setSelectedSalesChannelsStore(null);
        setDateRangeStore(null);
        setTimeout(() => setPageReload(false), 20);
    }, [session]);

    const slas = [
        { name: 'resume', label: 'Resumen' },
        { selectedSla: ['express'], icon: 'spid', name: 'despachoExpress', label: 'Despacho Express', color: 'rgba(255, 104, 0, 0.6)' },
        { selectedSla: ['domicilio', 'especial'], name: 'despachoDomicilio', label: 'Agendamiento', color: 'rgba(77, 174, 169, 0.77)' },
        { selectedSla: ['dinamico'], name: 'despachoDinamico', label: 'Despacho Dinamico', color: 'rgba(86, 101, 245, 0.65)', icon: 'box' },
        { selectedSla: ['auto'], name: 'clickAlAuto', label: 'Retiro en auto', color: '#f8da76' },
        { selectedSla: ['tienda'], name: 'retiroEnTienda', label: 'Retiro en tienda', color: '#cb93cc' }
    ];

    // These hooks are used by query variables
    const [dateRange, setDateRange] = useState([moment(), moment()]);
    const [selectedSalesChannels, setSelectedSalesChannels] = useState([]);
    const [pageReload, setPageReload] = useState(false);

    // These hooks are used to store new values received from filters
    const [dateRangeStore, setDateRangeStore] = useState(null);
    const [selectedSalesChannelsStore, setSelectedSalesChannelsStore] = useState(null);
    const [salesChannelKind, setSalesChannelKind] = useState(null);
    const [selectedCompanyGroup, onChangeSelectedCompanyGroup] = useState({ label: 'Jumbo', value: 'Jumbo' });

    // Firebase: Instance performance monitoring service
    const [traceOrdersMonitorInitialLoad] = useState(firebasePerformance.trace('ORDERS_MONITOR_INITIAL_LOAD'));
    const [traceOrdersMonitorAction, setTraceOrdersMonitorAction] = useState({});

    useEffect(() => {
        // Firebase: Starts the timing for the trace instance
        traceOrdersMonitorInitialLoad.start();
    }, [traceOrdersMonitorInitialLoad]);

    useEffect(() => {
        // Firebase: Starts the timing for the trace instance
        get(traceOrdersMonitorAction, 'state') === 1 && traceOrdersMonitorAction.start();
    }, [traceOrdersMonitorAction]);

    const filterBySalesChannelKind = () => {
        const validKind = salesChannelKind === null || Object.entries(salesChannelKind).length === 0 || get(salesChannelKind, 'value') === 'todos';
        const filterByKind = ({ kind }) => kind === get(salesChannelKind, 'value');
        if (selectedSalesChannelsStore.length !== 0) {
            if (validKind) {
                return selectedSalesChannelsStore;
            } else if (size(sessionSalesChannels) !== size(selectedSalesChannelsStore)) {
                const toFilter = [];
                map(sessionSalesChannels, (sessionChannel) => {
                        map(selectedSalesChannelsStore, (StoreCannel) => {
                            if (get(sessionChannel, 'value') === get(StoreCannel, 'value')) return toFilter.push(sessionChannel);
                        });
                    });

                return filter(toFilter, filterByKind);
                }
        }
        return validKind ? sessionSalesChannels : filter(sessionSalesChannels, filterByKind);
    };

    const variables = {
        filters: {
            salesChannel: map(selectedSalesChannelsStore, ({ label, value, kind }) => {
                return {
                    id: value,
                    name: label
                };
            }),
            range: {
                init: `${dateRange[0].format('YYYY-MM-DD')} 00:00:00`,
                end: `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`
            }
        }
    };

    const setFormFieldValue = ({ field, value }) => {
        switch (field) {
        case 'salesChannel':
            if (size(selectedSalesChannels) > 0) return setSelectedSalesChannelsStore(value);
            return setSelectedSalesChannels(value);
        case 'dateRange':
            return setDateRangeStore(value);
        case 'salesChannelKind':
            return setSalesChannelKind(value);
        }
    };

    const updateQueryVariables = () => {
        // Firebase: Instance performance monitoring service
        setTraceOrdersMonitorAction(firebasePerformance.trace('ORDERS_MONITOR_ACTION'));

        setDateRange(dateRangeStore || dateRange);
        if (!arrayIsNill(selectedSalesChannelsStore)) setSelectedSalesChannels(selectedSalesChannelsStore || selectedSalesChannels);

        setSelectedSalesChannels(filterBySalesChannelKind());
    };

    return (
        <HasPermissions
            to={['orders-monitor:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' style={{ minHeight: '90vh' }} />}
        >
            <Main background='f4f4f4' withFooter>
                <ModuleAccessLogger module='Resumen de Tiendas' />
                {
                    !pageReload && (
                        <MediaListener values={['18%', '100%', '100%']}>
                            {({ currentValue }) => {
                                const isMobile = includes([currentValue], '100%');
                                // 6 cards (Resumen + 5 SLAs): 5 gaps de 10px en el flex
                                const cardWidth = isMobile ? '100%' : 'calc((100% - 50px) / 6)';
                                return (
                                    <>
                                        <MobilePageTitle text='Resumen de Tiendas' />
                                        <HasPermissions to={['orders-monitor:filter&search']}>
                                            <Filters
                                                currentValue={currentValue}
                                                onChangeSalesChannels={({ value }) => setFormFieldValue({ field: 'salesChannel', value })}
                                                onChangeDateRange={value => setFormFieldValue({ field: 'dateRange', value })}
                                                onChangeFilters={updateQueryVariables}
                                                onChangeSelectedCompanyGroup={({ value }) => {
                                                    onChangeSelectedCompanyGroup(value);
                                                }}
                                                selectedCompanyGroup={selectedCompanyGroup}
                                            />
                                        </HasPermissions>
                                        {
                                            size(selectedSalesChannels) > 0 && (
                                                <Query
                                                    query={GET_ORDERS_COUNTERS}
                                                    variables={variables}
                                                    pollInterval={60000}
                                                    onCompleted={(queryData) => {
                                                        // Firebase: Stops the timing of the trace instance
                                                        get(traceOrdersMonitorInitialLoad, 'state') === 2 && traceOrdersMonitorInitialLoad.stop();
                                                        get(traceOrdersMonitorAction, 'state') === 2 && traceOrdersMonitorAction.stop();
                                                        // Almacenar los datos para exportar
                                                        const counterData = get(queryData, 'getOrdersCounters.data');
                                                        if (counterData) setCountersData(counterData);
                                                    }}
                                                >
                                                    {({ data: results, loading, networkStatus }) => {
                                                        const { message, status, data } = get(results, 'getOrdersCounters', {});
                                                        const bySalesChannel = get(data, 'bySalesChannel', {});
                                                        return (
                                                            <If
                                                                condition={status !== '200' && !isNil(status)}
                                                                then={(
                                                                    <NotFound
                                                                        firstTextLine='No se encontraron resultados'
                                                                        icon='cross'
                                                                        iconBackgroundColor='#eaeaea'
                                                                        backgroundColor='#F4F4F4'
                                                                    />
                                                                )}
                                                                else={(
                                                                    <Loading loading={networkStatus === 6 ? false : loading}>
                                                                        {/* All SalesChannels Cards */}
                                                                        <FlexContainer direction='column' style={{ padding: '0px 23px' }}>
                                                                            { (size(bySalesChannel) > 1) && (
                                                                                <Accordion
                                                                                    title={<Text text={get(data, 'totals.name')} size={14} strong nowrap paddingRight={10} />}
                                                                                    closedBackground='white'
                                                                                    defaultOpened
                                                                                    background='F4F4F4'
                                                                                    rightHeader={!isMobile && <HorizontalResume data={get(data, 'totals.resume', [])} />}
                                                                                >
                                                                                    <FlexContainer direction='row' wrap='true' gap={10} paddingHorizontal={0} paddingVertical={5}>
                                                                                        <If
                                                                                            condition={isMobile}
                                                                                            then={
                                                                                                <SlaCardMobile
                                                                                                    data={get(data, 'totals', [])}
                                                                                                    dateRange={[`${dateRange[0].format('YYYY-MM-DD')} 00:00:00`, `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`]}
                                                                                                    selectedSalesChannels={selectedSalesChannelsStore}
                                                                                                />
                                                                                            }
                                                                                            else={map(slas, (sla, index) =>
                                                                                                <SlaCard
                                                                                                    key={index}
                                                                                                    {...sla}
                                                                                                    {...head(filter(get(data, 'totals'), (_, index) => index === get(sla, 'name')))}
                                                                                                    width={cardWidth}
                                                                                                    dateRange={[`${dateRange[0].format('YYYY-MM-DD')} 00:00:00`, `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`]}
                                                                                                    selectedSalesChannels={selectedSalesChannelsStore}
                                                                                                />
                                                                                            )}
                                                                                        />
                                                                                    </FlexContainer>
                                                                                </Accordion>
                                                                            ) }
                                                                            {/* Cards for each SalesChannel */}
                                                                            { map(
                                                                                orderBy(bySalesChannel, (item) => get(item, 'resume.pendientePicking', 0), 'desc'),
                                                                                ({ name, id, __typename, ...storeInfo }, key) => (
                                                                                <Accordion
                                                                                    key={key}
                                                                                    title={<Text text={name} size={14} strong nowrap paddingRight={10} />}
                                                                                    closedBackground='white'
                                                                                    background='F4F4F4'
                                                                                    defaultOpened={size(bySalesChannel) <= 1}
                                                                                    rightHeader={!isMobile && <HorizontalResume data={get(storeInfo, 'resume', [])} />}
                                                                                >
                                                                                    <FlexContainer direction='row' wrap='true' style={{ gap: 10, padding: '5px 0px' }}>
                                                                                        <If
                                                                                            condition={isMobile}
                                                                                            then={
                                                                                                <SlaCardMobile
                                                                                                    data={{ name, id, ...storeInfo }}
                                                                                                    dateRange={[`${dateRange[0].format('YYYY-MM-DD')} 00:00:00`, `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`]}
                                                                                                />
                                                                                            }
                                                                                            else={map(slas, (slaAttributes) => {
                                                                                                const info = get(storeInfo, slaAttributes.name, {});
                                                                                                return (
                                                                                                    <SlaCard
                                                                                                        key={slaAttributes.name}
                                                                                                        label={get(slaAttributes, 'label', '')}
                                                                                                        color={get(slaAttributes, 'color', '#ffffff')}
                                                                                                        selectedSla={get(slaAttributes, 'selectedSla', null)}
                                                                                                        name={slaAttributes.name}
                                                                                                        icon={get(slaAttributes, 'icon')}
                                                                                                        salesChannel={id}
                                                                                                        {...info}
                                                                                                        width={cardWidth}
                                                                                                        dateRange={[`${dateRange[0].format('YYYY-MM-DD')} 00:00:00`, `${dateRange[1].format('YYYY-MM-DD')} 23:59:59`]}
                                                                                                        selectedSalesChannels={selectedSalesChannelsStore}
                                                                                                    />
                                                                                                );
                                                                                            })}
                                                                                        />
                                                                                    </FlexContainer>
                                                                                </Accordion>
                                                                            )) }
                                                                        </FlexContainer>
                                                                    </Loading>
                                                                )}
                                                            />
                                                        );
                                                    }}
                                                </Query>
                                            )
                                        }
                                    </>
                                );
                            }}
                        </MediaListener>
                    )
                }
            </Main>
        </HasPermissions>
    );
};

OrdersMonitor.propTypes = {
    session: object,
    client: object,
    firebasePerformance: object
};

export default withRouter(withFirebase(withApollo(OrdersMonitor)));
